import React, { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { HeartHandshake, CheckCircle2, Phone } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { growthApi, track } from "../api";
import useSeo from "../hooks/useSeo";

const PROGRAMS = ["Individual Options", "Level One", "SELF", "Next Generation MyCare", "PASSPORT", "Private Pay", "Unsure", "Other"];
const RELATIONSHIPS = ["Parent", "Adult Child", "Sibling", "Grandparent", "Other Relative", "Friend", "Other"];
const AGE_GROUPS = ["Under 18", "18+", "Prefer not to say"];
const SERVICE_OPTIONS = ["HPC", "Participant Directed HPC", "Respite", "Transportation", "Shared Living", "Other", "Unsure"];
const CONTACT_METHODS = ["Phone call", "Text message", "Email"];

const OPTION_POINTS = [
  "You or your family member receives DODD waiver services",
  "Participant-directed services are included or may be considered",
  "You already have a trusted person you would like involved in care",
  "Your SSA/care team determines the service is appropriate",
  "The worker can satisfy applicable eligibility/training/background requirements",
];

const EMPTY = {
  name: "", phone: "", email: "", county: "", program: "",
  hasCaregiver: "", relationship: "", ageGroup: "", services: [], hasSSA: "", preferredContact: "",
};

const FamilyCaregiver = () => {
  useSeo({
    title: "Can a Family Member Become Your Paid Caregiver in Ohio? | Caregivers Alliance Homecare",
    description: "Some Ohio Medicaid and DODD participant-directed programs may allow a qualified family member or friend to provide approved services. Learn how eligibility works and ask Caregivers Alliance Homecare about next steps. Call (614) 526-1548.",
    keywords: "paid family caregiver Ohio, can family member be caregiver Ohio, Ohio DODD family caregiver, SELF waiver family caregiver, participant directed caregiver Ohio, participant directed HPC Ohio",
    path: "/family-caregiver",
  });
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { track("family_caregiver_visit", ""); }, []);

  const toggleService = (s) =>
    setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await growthApi.familyCaregiverCreate(form);
      toast({ title: "Inquiry received!", description: "Our team will review your situation and contact you about next steps." });
      setForm(EMPTY);
    } catch {
      toast({ title: "Something went wrong", description: "Please call us at (614) 526-1548.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="brand-gradient text-white">
        <div className="mx-auto max-w-7xl px-4 py-16">
          <h1 className="font-display font-extrabold text-4xl md:text-5xl max-w-3xl">
            Can a Family Member Become Your Paid Caregiver in Ohio?
          </h1>
          <p className="mt-5 text-white/90 max-w-3xl leading-relaxed">
            Some Ohio Medicaid and DODD participant-directed programs may allow an individual to select a trusted
            family member, friend, or other qualified person to provide approved services. Eligibility depends on the
            individual's waiver, service plan, relationship, provider qualifications, program rules, and authorization.
          </p>
          <a href="#byoc-form" className="inline-block mt-7">
            <Button data-testid="fc-hero-cta" className="rounded-full bg-white text-[#0E5A72] hover:bg-white/90 h-12 px-8 font-semibold">
              I ALREADY HAVE SOMEONE WHO HELPS ME
            </Button>
          </a>
        </div>
      </section>

      <section className="section bg-white">
        <div className="mx-auto max-w-7xl px-4 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <span className="text-[#2E9E6A] font-semibold uppercase tracking-wide text-sm">Participant-Directed Care</span>
            <h2 className="font-display font-bold text-3xl text-[#0E2A33] mt-2">You may have options if:</h2>
            <div className="mt-6 space-y-3">
              {OPTION_POINTS.map((p) => (
                <div key={p} className="flex items-start gap-3 bg-[#F7FBFC] rounded-xl border border-slate-100 p-4">
                  <CheckCircle2 className="h-5 w-5 text-[#2E9E6A] shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-sm">{p}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 bg-[#E9F6EF] border border-emerald-200 rounded-2xl p-6">
              <div className="flex items-start gap-3">
                <HeartHandshake className="h-6 w-6 text-[#23875A] shrink-0" />
                <p className="text-sm text-[#0E2A33] leading-relaxed">
                  <span className="font-semibold">Caregivers Alliance can help you understand the next steps</span>{" "}
                  and determine whether our agency can participate in the arrangement. Eligibility is never automatic —
                  every situation is reviewed against the individual's waiver, service plan and program rules.
                </p>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-4">
              Submitting this form does not guarantee eligibility, employment, or payment. Please do not include
              diagnosis or detailed medical information.
            </p>
          </div>

          <form id="byoc-form" onSubmit={submit} data-testid="fc-form" className="bg-white rounded-2xl border border-slate-100 shadow-lg p-7 space-y-4 scroll-mt-24">
            <h3 className="font-display font-bold text-xl text-[#0E2A33]">I already have someone who helps me</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Individual / Family Contact Name</Label>
                <Input data-testid="fc-name-input" required value={form.name} onChange={(e) => set("name")(e.target.value)} className="mt-1" placeholder="Full name" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input data-testid="fc-phone-input" required value={form.phone} onChange={(e) => set("phone")(e.target.value)} className="mt-1" placeholder="(614) 555-0100" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input data-testid="fc-email-input" type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} className="mt-1" placeholder="you@email.com" />
              </div>
              <div>
                <Label>County</Label>
                <Input data-testid="fc-county-input" value={form.county} onChange={(e) => set("county")(e.target.value)} className="mt-1" placeholder="e.g. Franklin" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Current Program / Waiver</Label>
                <Select value={form.program} onValueChange={set("program")}>
                  <SelectTrigger data-testid="fc-program-select" className="mt-1"><SelectValue placeholder="Select program" /></SelectTrigger>
                  <SelectContent>{PROGRAMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Caregiver already interested?</Label>
                <Select value={form.hasCaregiver} onValueChange={set("hasCaregiver")}>
                  <SelectTrigger data-testid="fc-hascaregiver-select" className="mt-1"><SelectValue placeholder="Yes / No" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Relationship</Label>
                <Select value={form.relationship} onValueChange={set("relationship")}>
                  <SelectTrigger data-testid="fc-relationship-select" className="mt-1"><SelectValue placeholder="Select relationship" /></SelectTrigger>
                  <SelectContent>{RELATIONSHIPS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Age of person receiving services</Label>
                <Select value={form.ageGroup} onValueChange={set("ageGroup")}>
                  <SelectTrigger data-testid="fc-age-select" className="mt-1"><SelectValue placeholder="Select age group" /></SelectTrigger>
                  <SelectContent>{AGE_GROUPS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Requested Services</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {SERVICE_OPTIONS.map((s) => (
                  <label key={s} className="flex items-center gap-2 text-sm text-slate-700 bg-[#F7FBFC] rounded-lg border border-slate-100 px-3 py-2 cursor-pointer">
                    <Checkbox data-testid={`fc-service-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} checked={form.services.includes(s)} onCheckedChange={() => toggleService(s)} />
                    {s}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Do you have an SSA / case manager?</Label>
                <Select value={form.hasSSA} onValueChange={set("hasSSA")}>
                  <SelectTrigger data-testid="fc-ssa-select" className="mt-1"><SelectValue placeholder="Yes / No / Unsure" /></SelectTrigger>
                  <SelectContent>
                    {["Yes", "No", "Unsure"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Preferred Contact Method</Label>
                <Select value={form.preferredContact} onValueChange={set("preferredContact")}>
                  <SelectTrigger data-testid="fc-contact-select" className="mt-1"><SelectValue placeholder="How should we reach you?" /></SelectTrigger>
                  <SelectContent>{CONTACT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <Button data-testid="fc-submit-btn" type="submit" disabled={busy} className="w-full bg-[#2E9E6A] hover:bg-[#23875A] text-white rounded-full h-12">
              {busy ? "Sending..." : "Ask About Family Caregiver Options"}
            </Button>
            <p className="text-xs text-slate-400 text-center">
              We do not collect diagnosis or detailed medical information through this form.
            </p>
          </form>
        </div>
      </section>

      <section className="section bg-[#F7FBFC]">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-[#0E2A33]">Prefer to talk it through?</h2>
          <p className="text-slate-600 mt-3">
            Call us and we'll walk through your program, your caregiver situation, and whether our agency can participate.
          </p>
          <a href="tel:6145261548" onClick={() => track("call_click", form.county)} className="inline-block mt-5">
            <Button className="rounded-full bg-[#0E5A72] hover:bg-[#0A4557] text-white h-12 px-8"><Phone className="mr-2 h-4 w-4" /> (614) 526-1548</Button>
          </a>
        </div>
      </section>
    </>
  );
};

export default FamilyCaregiver;
